// ─── useEntitlements ─────────────────────────────────────────────────────────
// The active tenant's plan entitlements, read once per tenant from
// GET /api/v1/credits/balance.
//
// ⭐ This hook is the WALLET: credits and the pre-flight verdict, nothing else.
//
// It does not answer "which output types may this workspace run" — that moved
// to hooks/useOutputEntitlements.js and GET /api/v1/entitlements when the
// backend withdrew `allowed_formats` (2026-09-20). ⛔ Do not reintroduce a
// format concern here: the two questions have different endpoints, different
// refusal codes and different remedies.
//
// ⛔ Nor does it answer "how many seats may this company fill": /balance serves
// no seat field at all, and a seat is a per-CUSTOMER fact that arrives on the
// GET /tenant-admin/users envelope (tenantAdminApi.js -> fetchTenantSeats).
// Same rule as above — do not reintroduce a seat concern here.
//
// ⏰ The name is now wrong and is kept only to hold this pass small; it is
// renamed to useWallet in the credits/billing item.
//
// ⚠️ 2026-09-23: the backend renamed `monthly_credits` -> `allowance_credits`
// and added the two pots (`allowance_remaining` / `purchased_remaining`).
// ⛔ Do not reintroduce `monthlyCredits`: the wallet is TWO POTS plus their sum,
// and a single "monthly" figure cannot express it.
//
// One fetch per active tenant, no polling: /balance is served
// `Cache-Control: private, max-age=60`, so that is the intended volume.
//
// ⚠️ Two rules this hook exists to hold:
//   1. **Fail open.** A credits outage must never blank the catalogue or block
//      generation. On ANY error (and while loading) `canGenerate` is true —
//      the same for an old backend that does not serve the field yet.
//   2. **No tenant, no call.** /balance answers 400 TENANT_REQUIRED without one
//      (a superadmin before the picker), which is noise, not information.

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchCreditBalance } from '../services/api/creditsApi.js';

// The permissive baseline: what we report before the fetch lands, when there is
// no tenant to ask about, and when the fetch fails.
const UNRESTRICTED = {
  canGenerate: true,
  blockReason: null,
  unlimited: false,
  expired: false,
  periodEnd: null,
  customerId: null,
  // The wallet numbers, verbatim decimal strings as /balance serves them.
  // Here so that EVERY reader of the wallet — the balance card, the sidebar
  // meter — draws from this one fetch and is refreshed by the same epoch.
  // ⚠️ null = "unknown", never 0: a meter must draw nothing on null.
  remainingCredits: null,     // spendable total = allowance + purchased
  allowanceCredits: null,     // what the period's GRANT granted (RENAMED 2026-09-23
                              //   from monthlyCredits / balance.monthly_credits)
  allowanceRemaining: null,   // the period pot — resets at the next grant
  purchasedRemaining: null,   // the all-time pot — never expires, MAY BE NEGATIVE
  consumedCredits: null,
  // Whether an OPEN subscription exists (`has_plan`). Read here — not from a
  // page's own /balance call — by anything that must appear only with a live
  // plan (the top-up card). ⛔ Distinct from `canGenerate`: a plan with
  // CREDITS_EXHAUSTED is live (hasPlan true) and cannot generate; that is
  // exactly when a top-up is wanted, so the two must never be conflated.
  hasPlan: false,
};

// ─── Module-level nudges ─────────────────────────────────────────────────────
// A run refusal is authoritative about entitlements, and it lands in the
// orchestrator's error path while the wizard holds its OWN instance of this
// hook. Rather than thread a setter through six call sites — the same reasoning
// that put the tone-preset registry at module scope in utils/toneMigration.js —
// a refusal bumps an epoch here and every mounted instance re-reads /balance.
let epoch = 0;
const listeners = new Set();

function publish() {
  epoch += 1;
  listeners.forEach(fn => fn(epoch));
}

/**
 * Re-read /balance on every mounted instance.
 *
 * Called after a 402 on the run, which is EXPECTED rather than a bug: the
 * wallet belongs to the customer, every workspace it owns spends from it, and
 * /balance is served `Cache-Control: private, max-age=60`. So `can_generate:
 * true` followed by a refusal only means our snapshot aged. Resync the tile —
 * and never auto-retry the run off the back of it.
 */
export function refreshEntitlements() {
  publish();
}

// Read the entitlement slice out of a /balance payload. Every field tolerates
// absence (an old backend predates them) and falls back to today's behaviour.
function readEntitlements(balance) {
  return {
    // Absent `can_generate` (old backend) must not read as blocked.
    canGenerate: balance?.can_generate !== false,
    blockReason: balance?.block_reason ?? null,
    unlimited: balance?.unlimited === true,
    expired: balance?.expired === true,
    periodEnd: balance?.period_end ?? null,
    customerId: balance?.customer_id ?? null,
    // Verbatim: parseDecimal/formatCredits live at the render site, as today.
    remainingCredits: balance?.remaining_credits ?? null,
    // ⚠️ RENAMED by the backend (slice 3): `monthly_credits` -> `allowance_credits`.
    // Under yearly plans the old name was wrong, and the value is now what the
    // period's GRANT ROW actually granted rather than what the plan offers today.
    allowanceCredits: balance?.allowance_credits ?? null,
    // ⛔ The TWO POTS are different answers, not two views of one number:
    // `allowanceRemaining` is period-scoped and is LOST at the next grant;
    // `purchasedRemaining` never expires, survives a cancellation, and MAY BE
    // NEGATIVE (a standing deficit). Their sum is `remainingCredits`, which is
    // the only figure the server's gate tests. ⛔ Never gate on one pot.
    allowanceRemaining: balance?.allowance_remaining ?? null,
    purchasedRemaining: balance?.purchased_remaining ?? null,
    consumedCredits: balance?.consumed_credits ?? null,
    hasPlan: balance?.has_plan === true,
  };
}

export function useEntitlements() {
  const { activeTenant } = useAuth();
  const tenantId = activeTenant?.tenant_id;

  const [state, setState] = useState({
    ...UNRESTRICTED,
    loading: !!tenantId,
    error: null,
  });

  // Subscribe to the module-level nudges above. A bump re-runs the fetch effect
  // below on EVERY mounted instance, which is what lets a refusal caught in the
  // orchestrator resync the wallet everywhere it is displayed.
  const [tick, setTick] = useState(epoch);
  useEffect(() => {
    const onPublish = value => setTick(value);
    listeners.add(onPublish);
    return () => { listeners.delete(onPublish); };
  }, []);

  useEffect(() => {
    // No tenant picked yet → don't call, don't spin, offer everything.
    if (!tenantId) {
      setState({ ...UNRESTRICTED, loading: false, error: null });
      return undefined;
    }

    // `cancelled` is what keeps a late response from landing on the wrong
    // tenant: a switch re-runs this effect, the previous run's flag flips, and
    // its resolution is dropped instead of overwriting the new tenant's answer.
    let cancelled = false;
    setState(prev => ({ ...prev, loading: true, error: null }));

    fetchCreditBalance({ tenant: tenantId })
      .then(balance => {
        if (cancelled) return;
        setState({ ...readEntitlements(balance), loading: false, error: null });
      })
      .catch(err => {
        if (cancelled) return;
        // Fail open — see rule 1. The error is exposed for diagnostics only;
        // no caller may turn it into a lock.
        setState({ ...UNRESTRICTED, loading: false, error: err });
      });

    return () => { cancelled = true; };
  }, [tenantId, tick]);

  return state;
}

export default useEntitlements;
